import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { cn } from '../../lib/utils'
import { format } from 'date-fns'
import React, { useState } from 'react'

export interface DatePickerProps {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  error?: boolean
}

const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  ({ value, onChange, placeholder = 'Pick a date', disabled, className, error }, ref): React.ReactElement => {
    const [open, setOpen] = useState(false)

    const handleSelect = (selectedDate: Date | undefined): void => {
      if (!selectedDate) return

      // Normalize the date to avoid timezone issues by setting it to noon UTC
      const normalizedDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 12, 0, 0, 0)
      onChange?.(normalizedDate)

      setOpen(false)
    }

    // Disable future dates
    const today = new Date()
    today.setHours(23, 59, 59, 999)

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            type="button"
            disabled={disabled}
            className={cn(
              'flex h-9 w-full items-center justify-start rounded-md bg-secondary-gray px-3 py-2 text-sm',
              'font-evogria font-normal text-light-gray placeholder:text-mushroom',
              'focus-visible:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50',
              !value && 'text-mushroom',
              error && 'border border-danger',
              className,
            )}
          >
            {value ? format(value, 'dd/MM/yyyy') : <p className="text-sm font-evogria text-mushroom uppercase">{placeholder}</p>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={handleSelect}
            defaultMonth={value}
            captionLayout="dropdown"
            disabled={(date) => date > today}
          />
        </PopoverContent>
      </Popover>
    )
  },
)

DatePicker.displayName = 'DatePicker'

export { DatePicker }
